import React, { useState } from 'react'
import { faChevronLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import ProgressBar from '../../../components/ProgressBar'
import { selectIsRoleFlow, selectSkillName, selectTargetRole, setSpeedLevel, setTimeLevel, updateUserSpeedLevel, updateUserTimeLevel, selectSpeedLevel, selectTimeLevel } from '../../../redux/skillSlice'
import { capitalise } from '../../../utilities/constants'
import { toast } from 'react-toastify'
import { selectInitSessionUserDetails } from '../../../redux/onboardingUserDetails'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'
import { useEffect } from 'react'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../../redux/newAnalysisSlice'

const OnboardingStep4 = () => {
    const navigate = useNavigate()
    const targetRole = useSelector(selectTargetRole)
    const skillName = useSelector(selectSkillName)
    const userSessionDetails = useSelector(selectInitSessionUserDetails)
    const isRoleFlow = useSelector(selectIsRoleFlow);

    const [selectedTimeline, setSelectedTimeline] = useState(null)
    const [weeklyHours, setWeeklyHours] = useState(4)

    const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited)
    const isPricingPageVisited = useSelector(selectIsPricingPageVisited)

    // Get speed level and time level from store
    const speedLevelFromStore = useSelector(selectSpeedLevel)
    const timeLevelFromStore = useSelector(selectTimeLevel)

    const dispatch = useDispatch()


    useEffect(() => {
        if (isAnalysisPageVisited) {
          navigate('/onboarding/analysis')
        }
    
        else if (isPricingPageVisited) {
          navigate('/onboarding/pricing')
        }
    
      }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, navigate])
    // Track page load
    useEffect(() => {
        Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP4_PAGE_LOADED, {
            flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
            target_role: targetRole || '',
            skill_name: skillName || '',
            session_id: userSessionDetails?.session_id || '',
            current_step: isRoleFlow ? 6 : 4,
            total_steps: isRoleFlow ? 8 : 6,
            timestamp: new Date().toISOString()
        })
    }, [isRoleFlow, targetRole, skillName, userSessionDetails?.session_id])

    // Initialize values from store
    useEffect(() => {
        // Set timeline from store
        if (speedLevelFromStore && !selectedTimeline) {
            const matchingOption = timelineOptions.find(option => option.value === speedLevelFromStore);
            if (matchingOption) {
                setSelectedTimeline(matchingOption);
            }
        }
        
        // Set weekly hours from store
        if (timeLevelFromStore) {
            const parsedTime = typeof timeLevelFromStore === 'number' 
                ? timeLevelFromStore 
                : parseInt(timeLevelFromStore);
            
            // Ensure the time is within valid range (2-20 hours)
            if (!isNaN(parsedTime) && parsedTime >= 2 && parsedTime <= 20) {
                setWeeklyHours(parsedTime);
            }
        }
    }, [speedLevelFromStore, timeLevelFromStore, selectedTimeline]);

    // Timeline options
    const timelineOptions = [
        {
            value: 'two_weeks',
            text: '2 Weeks',
            emoji: 'https://assets.learntube.ai/files/personalise/simple-line-icons_rocket.webp'
        },
        {
            value: 'four_weeks',
            text: '4 Weeks',
            emoji: 'https://assets.learntube.ai/files/personalise/mdi_speedometer.webp'
        },
        {
            value: 'eight_weeks',
            text: '8 Weeks',
            emoji: 'https://assets.learntube.ai/files/personalise/ri_run-fill.webp'
        },
        {
            value: 'flexible',
            text: 'Flexible',
            emoji: 'https://assets.learntube.ai/files/personalise/streamline-flex_tea-cup.webp'
        }
    ]

    const handleOptionClick = (option) => {
        try {
            setSelectedTimeline(option)
            dispatch(setSpeedLevel(option.value))
            dispatch(updateUserSpeedLevel({
                session_id: userSessionDetails?.session_id,
                timeline: option.value
            }))

            // Track timeline selection
            Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP4_TIMELINE_SELECTED, {
                selected_timeline: option.value,
                timeline_text: option.text,
                flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
                target_role: targetRole || '',
                skill_name: skillName || '',
                session_id: userSessionDetails?.session_id || '',
                timestamp: new Date().toISOString()
            })
        }
        catch (error) {
            console.error('Error in handleOptionClick:', error)
        }
    }

    const handleSliderChange = (e) => {
        const newHours = parseInt(e.target.value)
        setWeeklyHours(newHours)

        // Track weekly hours change (with minimal throttling to avoid excessive events)
        Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP4_WEEKLY_HOURS_CHANGED, {
            weekly_hours: newHours,
            hours_display: getHoursDisplay(newHours),
            selected_timeline: selectedTimeline?.value || '',
            flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
            target_role: targetRole || '',
            skill_name: skillName || '',
            session_id: userSessionDetails?.session_id || '',
            timestamp: new Date().toISOString()
        })
    }

    const getHoursDisplay = (hours) => {
        if (hours >= 20) return '20+ hrs'
        return `${hours} hrs`
    }

    const handleContinueClick = () => {
        try {
            // Track continue button click with complete preferences
            Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP4_CONTINUE_CLICKED, {
                selected_timeline: selectedTimeline?.value || '',
                timeline_text: selectedTimeline?.text || '',
                weekly_hours: weeklyHours,
                hours_display: getHoursDisplay(weeklyHours),
                flow_type: isRoleFlow ? 'role_flow' : 'skill_flow',
                target_role: targetRole || '',
                skill_name: skillName || '',
                session_id: userSessionDetails?.session_id || '',
                next_step: '/onboarding/userdetails',
                timestamp: new Date().toISOString()
            })

            dispatch(setTimeLevel(weeklyHours))
            dispatch(updateUserTimeLevel({
                session_id: userSessionDetails?.session_id,
                weekly_time: getHoursDisplay(weeklyHours)
            }))
            navigate('/onboarding/userdetails')
        }
        catch (error) {
            console.error('Error in handleContinueClick:', error)
        }
    }

    return (
        <div className='mt-8 w-full md:max-w-[45rem] flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col justify-between items-center'>

            <div className='w-full flex flex-col justify-start items-center'>
                <div className='w-full flex flex-col justify-start items-center'>
                    <ProgressBar currentStep={isRoleFlow ? 6 : 4} totalSteps={isRoleFlow ? 8 : 6} stepText={`Step ${isRoleFlow ? 6 : 4}/${isRoleFlow ? 8 : 6} to ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`} />
                </div>

                <div className='w-full mt-4 flex flex-col p-[2px] rounded-[1.5rem]' style={{
                    backgroundImage: `linear-gradient(
        to right,
        rgba(64, 106, 255, 0.3) 0%,
        rgba(64, 106, 255, 1) 56%,
        rgba(64, 106, 255, 0.3) 100%
      )`
                }}>
                    {/* Main content card */}
                    <div className='flex flex-col p-6 rounded-[1.5rem] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
                        <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
                            style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />
                        {/* Content */}
                        <div className='flex flex-col gap-6 relative z-10'>
                            {!selectedTimeline ? (
                                <>
                                    {/* Header */}
                                    <div className='flex flex-col'>
                                        <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                                            How fast do you want to reach your goal?
                                        </h1>
                                    </div>

                                    {/* Timeline options */}
                                    <div className='flex flex-col gap-3 w-full'>
                                        {timelineOptions.map((option, index) => {
                                            const isSelected = selectedTimeline?.value === option.value

                                            return (
                                                <div
                                                    key={index}
                                                    className={`${isSelected
                                                        ? 'bg-[#7FC241]/20 border-[#7FC241] shadow-[0px_0px_2px_0px_#7fc241]'
                                                        : 'bg-[#406AFF]/15 border-[#406AFF]/70 hover:bg-[#406AFF]/25 hover:border-[#406AFF]'
                                                        } border border-solid flex gap-2 items-center p-4 rounded-lg cursor-pointer transition-all duration-200 w-full group`}
                                                    onClick={() => handleOptionClick(option)}
                                                >
                                                    {/* Radio button */}
                                                    <div className='relative w-[14px] h-[14px] shrink-0'>
                                                        <div className={`w-[14px] h-[14px] rounded-full border-2 ${isSelected
                                                            ? 'border-[#7FC241]'
                                                            : 'border-[#70AEFF] '
                                                            }`}>
                                                            <div className={`w-[6px] h-[6px] rounded-full absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all ${isSelected
                                                                ? 'bg-[#7FC241] opacity-100'
                                                                : 'bg-[#70AEFF] opacity-0 '
                                                                }`}></div>
                                                        </div>
                                                    </div>

                                                    {/* Option content */}
                                                    <div className='flex flex-1 items-center justify-between text-[14px] text-white'>
                                                        <span className='capitalize'>{option.text}</span>
                                                        <img src={option.emoji} alt={option.text} className='w-[18px] h-[18px]' />
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            ) : (
                                <>
                                    {/* Timeline indicator */}
                                    <div className='flex items-center text-[14px]'>
                                        <span className='text-white/50 font-medium'>Timeline: </span>
                                        <span className='capitalize text-white font-normal ml-1'>{selectedTimeline.text}</span>
                                    </div>

                                    {/* Main slider section */}
                                    <div className='flex flex-col items-start w-full'>
                                        <div className='flex flex-col gap-4 items-start w-full'>
                                            {/* Header */}
                                            <div className='flex flex-col items-start w-full'>
                                                <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                                                    How much time can you give weekly?
                                                </h1>
                                            </div>

                                            {/* Slider section */}
                                            <div className='mt-[70px] flex flex-col gap-2 items-center w-full'>
                                                {/* Slider track with tooltip */}
                                                <div className='flex flex-col gap-2 items-end w-full'>
                                                    {/* Slider track */}
                                                    <div className='flex gap-[2px] items-center justify-center w-full relative'>
                                                        {/* Value display tooltip - positioned above the handle */}
                                                        <div
                                                            className='absolute -top-16 flex flex-col items-center'
                                                            style={{
                                                                left: `${((weeklyHours - 2) / 18) * 100}%`,
                                                                transform: 'translateX(-50%)'
                                                            }}
                                                        >
                                                            <div className='backdrop-blur-sm bg-[#406AFF] border-[1.5px] border-[rgba(64,106,255,0.7)] border-solid flex items-center justify-center px-4 py-3 rounded-lg mb-[-2px] relative'>
                                                                <p className='font-semibold text-[14px] text-white tracking-[-0.14px] whitespace-nowrap'>
                                                                    {getHoursDisplay(weeklyHours)}
                                                                </p>
                                                            </div>
                                                            {/* Triangle pointer */}
                                                            <div className='flex items-center justify-center mb-[-2px] relative'>
                                                                <div className='w-0 h-0 border-l-[8.5px] border-r-[8.5px] border-t-[13px] border-l-transparent border-r-transparent border-t-[rgba(64,106,255,0.7)]'></div>
                                                            </div>
                                                        </div>

                                                        {/* Full slider track */}
                                                        <div className='relative h-1 w-full bg-[rgba(64,106,255,0.3)] rounded-full'>
                                                            {/* Active fill based on slider position */}
                                                            <div
                                                                className='absolute h-1 bg-[#406AFF] rounded-full top-0 left-0'
                                                                style={{
                                                                    width: `${((weeklyHours - 2) / 18) * 100}%`
                                                                }}
                                                            ></div>
                                                            {/* Slider handle */}
                                                            <div
                                                                className='absolute w-3 h-3 bg-[#406AFF] rounded-full border-2 border-white top-1/2 cursor-pointer transition-all duration-200 hover:scale-110 shadow-lg'
                                                                style={{
                                                                    left: `${((weeklyHours - 2) / 18) * 100}%`,
                                                                    transform: 'translate(-50%, -50%)',
                                                                    boxShadow: '0px 0px 6px 0px #406AFF'
                                                                }}
                                                            ></div>
                                                            {/* Hidden range input */}
                                                            <input
                                                                type="range"
                                                                min="2"
                                                                max="20"
                                                                value={weeklyHours}
                                                                onChange={handleSliderChange}
                                                                className='absolute inset-0 w-full h-full opacity-0 cursor-pointer'
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Labels */}
                                                    <div className='flex items-center justify-between font-normal text-[12px] text-[rgba(255,255,255,0.3)] w-full whitespace-nowrap'>
                                                        <div className='flex justify-center'>
                                                            <p>2 hrs</p>
                                                        </div>
                                                        <div className='flex justify-center'>
                                                            <p>20+ hrs</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Continue button */}
                                    <div className='flex flex-col gap-3 w-full'>
                                        <button
                                            onClick={handleContinueClick}
                                            className='bg-[#7FC241] hover:bg-[#8bd845] border-b-2 border-l-2 border-r-2 border-[#8bd845] h-12 rounded-lg font-semibold text-[#121212] text-lg transition-colors flex items-center justify-center gap-2 w-full'
                                        >
                                            Start Personalising Now
                                            <FontAwesomeIcon icon={faArrowRight} className='text-[#121212] text-sm' />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Personalisation indicator */}
            <div className='mt-6 flex flex-col gap-1 items-center justify-center'>
                <div className='flex items-center justify-center'>
                    <div className='animate-pulse text-[#83BEFF]'>
                        <img alt="Personalisation in progress" src="https://assets.learntube.ai/files/personalise/Vector%20(21).webp" className='w-7 h-5' />
                    </div>
                </div>
                <p className='italic font-medium text-[#83BEFF] text-[12px] text-center'>
                    18,700+ learners selected a pace that matched their schedule
                </p>
            </div>
        </div>
    )

}

export default OnboardingStep4